import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from './Layout';

const API = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

function fmtDate(str) {
  if (!str) return null;
  return new Date(str).toLocaleString('ru', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function StatusPill({ status, hasOwnApi, fallbackLabel }) {
  if (!hasOwnApi) return <span style={{ ...s.pill, ...s.pillGray }}>⚪ {fallbackLabel}</span>;
  if (status === 'active') return <span style={{ ...s.pill, ...s.pillGreen }}>🟢 Активен</span>;
  if (status === 'failed') return <span style={{ ...s.pill, ...s.pillRed }}>🔴 Ошибка</span>;
  return <span style={{ ...s.pill, ...s.pillGray }}>⚪ Не проверено</span>;
}

function SourceCard({ row, fallbackLabel, title }) {
  return (
    <div style={s.card}>
      <div style={s.cardHead}>
        <div style={s.cardTitle}>{title}</div>
        <StatusPill status={row.api_status} hasOwnApi={row.has_own_api} fallbackLabel={fallbackLabel} />
      </div>
      {row.has_own_api ? (
        <>
          <div style={s.row}><span style={s.rowLabel}>Endpoint (URL вашей МИС)</span><span style={s.mono}>{row.api_endpoint}</span></div>
          <div style={s.row}><span style={s.rowLabel}>Последний успешный опрос</span><span>{fmtDate(row.api_last_checked_at) || '—'}</span></div>
          {row.poll_error_count > 0 && (
            <>
              <div style={s.row}><span style={s.rowLabel}>Ошибок подряд</span><span style={s.errText}>{row.poll_error_count}</span></div>
              <div style={s.row}><span style={s.rowLabel}>Последний сбой</span><span>{fmtDate(row.last_poll_error_at) || '—'}</span></div>
              {row.last_poll_error && <div style={s.errDetail}>{row.last_poll_error}</div>}
            </>
          )}
        </>
      ) : (
        <div style={s.muted}>{fallbackLabel}</div>
      )}
    </div>
  );
}

export default function Api() {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [showInfo, setShowInfo] = useState(false);
  const headers = { Authorization: 'Bearer ' + localStorage.getItem('clinicToken') };

  useEffect(() => {
    axios.get(`${API}/clinic-cabinet/api-status`, { headers }).then(r => setD(r.data)).catch(() => {});
    // eslint-disable-next-line
  }, []);

  if (!d) return <Layout><div style={s.muted}>Загрузка…</div></Layout>;

  return (
    <Layout>
      <div style={s.header}>
        <h1 style={s.h1}>API</h1>
        <button style={s.btnGhost} onClick={() => setShowInfo(true)}>Изменить настройки API</button>
      </div>
      <div style={s.hint}>
        Refero ежедневно в 03:00 обращается к API вашей МИС, чтобы забрать данные об оплатах направленных пациентов.
        Здесь — статус этой интеграции и технические параметры для сверки с настройками вашей МИС.
      </div>

      {d.target_type === 'clinic' ? (
        <SourceCard row={d.own} title={d.name} fallbackLabel="Интеграция ещё не настроена Refero" />
      ) : (
        <>
          <SourceCard
            row={d.own}
            title={`${d.name} — общий API сети`}
            fallbackLabel="У сети нет общего API — данные собираются через API входящих учреждений"
          />
          {d.members.length > 0 && (
            <>
              <h2 style={s.h2}>Учреждения сети</h2>
              {d.members.map(m => (
                <SourceCard key={m.id} row={m} title={m.name} fallbackLabel="Опрашивается через общий API сети" />
              ))}
            </>
          )}
        </>
      )}

      {showInfo && (
        <div style={s.modalOverlay} onClick={() => setShowInfo(false)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <h3 style={s.modalTitle}>Изменение настроек API</h3>
            <p style={s.modalText}>
              Адрес API вашей МИС и ключ доступа настраивает только техподдержка Refero — самостоятельно
              изменить их в личном кабинете нельзя. Если нужно обновить адрес API или перевыпустить ключ,
              свяжитесь с поддержкой.
            </p>
            <div style={s.modalActions}>
              <button style={s.btnGhostModal} onClick={() => setShowInfo(false)}>Закрыть</button>
              <button style={s.btnPrimaryModal} onClick={() => navigate('/support')}>Связаться с техподдержкой</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

const s = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  h1: { color: '#1B2B4B', fontSize: 24, fontWeight: 700, margin: 0 },
  h2: { color: '#1B2B4B', fontSize: 17, fontWeight: 700, margin: '24px 0 12px' },
  hint: { fontSize: 12, color: '#94a3b8', background: '#fff', borderRadius: 8, padding: '10px 14px', marginBottom: 20, borderLeft: '3px solid #eef2f7', lineHeight: 1.5 },
  muted: { color: '#94a3b8', fontSize: 13 },
  btnGhost: { background: '#fff', color: '#b45309', border: '1px solid #fcd34d', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  card: { background: '#fff', borderRadius: 16, padding: 20, boxShadow: '0 4px 16px rgba(27,43,75,0.05)', marginBottom: 14 },
  cardHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 },
  cardTitle: { fontSize: 15, fontWeight: 700, color: '#1B2B4B' },
  pill: { display: 'inline-flex', alignItems: 'center', gap: 6, borderRadius: 20, padding: '4px 12px', fontSize: 12, fontWeight: 600 },
  pillGreen: { background: '#e9faf7', color: '#149e90' },
  pillRed: { background: '#fef2f2', color: '#dc2626' },
  pillGray: { background: '#f1f5f9', color: '#64748b' },
  row: { display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#1B2B4B', padding: '5px 0', borderTop: '1px solid #f4f7fb' },
  rowLabel: { color: '#94a3b8' },
  mono: { fontFamily: 'monospace', fontSize: 12 },
  errText: { color: '#dc2626', fontWeight: 600 },
  errDetail: { fontSize: 12, color: '#dc2626', marginTop: 6, whiteSpace: 'pre-wrap' },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(27,43,75,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: '#fff', borderRadius: 16, padding: 28, width: 440, maxWidth: '90vw', boxShadow: '0 20px 50px rgba(27,43,75,0.2)' },
  modalTitle: { color: '#1B2B4B', fontSize: 18, fontWeight: 700, margin: '0 0 12px' },
  modalText: { color: '#475569', fontSize: 14, lineHeight: 1.5, margin: '0 0 20px' },
  modalActions: { display: 'flex', justifyContent: 'flex-end', gap: 10 },
  btnGhostModal: { background: '#fff', color: '#1B2B4B', border: '1px solid #d7dee8', borderRadius: 10, padding: '10px 18px', fontSize: 14, cursor: 'pointer' },
  btnPrimaryModal: { background: '#2EC4B6', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 18px', fontSize: 14, fontWeight: 600, cursor: 'pointer', textDecoration: 'none', display: 'inline-block' },
};
